import { ChangeDetectorRef, Component, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, finalize, take, takeUntil } from 'rxjs/operators';
import { GoogleAutocompleteDirective } from 'src/app/_shared/directive/google-autocomplete.directive';
import { FilterService } from 'src/app/_shared/service/filter.service';
import { SavedSearchService } from 'src/app/_shared/service/saved-search.service';
import { FiltersState } from 'src/app/_shared/state/filter.state';
import { SavedSearchState } from 'src/app/_shared/state/saved-search.state';
import { WorkspaceState } from 'src/app/_shared/state/workspace.state';
import { PropertyFilterService } from 'src/app/property-filter/service/property-filter.service';
import { PropertyFilterState } from 'src/app/property-filter/state/property-filter.state';
import { ClientDashboardState } from '../../state/client-dashboard.state';

@Component({
  selector: 'app-save-search-sheet',
  templateUrl: './save-search-sheet.component.html',
  styleUrls: ['./save-search-sheet.component.scss'],
})
export class SaveSearchSheetComponent implements OnInit {
  @ViewChildren(GoogleAutocompleteDirective) googleDir;
  @ViewChild('searchField') searchField: any;

  onDestroyNotifier$ = new Subject();
  addPropertySearchValue = false;
  autoCompleteInput: any;
  showPropertyTable: false;
  disableSavedSearch: boolean = false;
  saveSearchForm: FormGroup;
  location: any;
  clientDetails: any;
  investorId: string;
  savingSearchInProgress: boolean = false;
  filterList: any;
  appliedFilters: any;
  showSaveSearchBtn: boolean = false;
  public isSaveSearchInProgress: boolean = false;

  constructor(
    private clientDashboardState: ClientDashboardState,
    private savedSearchState: SavedSearchState,
    private savedSearchService: SavedSearchService,
    private formBuilder: FormBuilder,
    private filtersState: FiltersState,
    public workspaceState: WorkspaceState,
    private filterService: FilterService,
    private _changeDetectorRef: ChangeDetectorRef,
    public propertyFilterService: PropertyFilterService,
    private propertyFilterState: PropertyFilterState
  ) {
    this.savedSearchState.$newSaveSearch.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {});
  }

  ngOnInit(): void {
    this.saveSearchForm = this.formBuilder.group({
      searchName: ['', [Validators.required]],
      addressSearch: ['', [Validators.required]],
    });
    this.propertyFilterState.savedFilters$.subscribe((filter) => {
      this.appliedFilters = this.propertyFilterService.getFilterDisplayList(filter);
      this.showSaveSearchBtn = this.appliedFilters.length > 0;
    });
    this.saveSearchForm.valueChanges.subscribe(values => {
      this.showSaveSearchBtn = Object.values(values).some(value =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      ) || this.appliedFilters.length > 0;
    });
    
    this.clientDashboardState.openAddSavedSearchSheet$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((status) => {
      this.addPropertySearchValue = status;
    });
    this.clientDashboardState.savedSearchLocation$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((value) => {
      if (value) {
        this.location = value;
        this.autoCompleteInput = value.fullAddress;
      }
    });

    this.clientDashboardState.activeClientDetails$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      this.clientDetails = res;
      this.investorId = this.clientDetails._id;
    });
    if (!this.filtersState.detailedFilterListValue) {
      this.filterService.setDetailedFilterForBaseFilter(this.workspaceState.activeWorkspaceModeValue?.defaultFilters);
      this.filterService.dataLayerFilterValue('clearAllFilter', {});
    }

    this.filtersState.detailedFilterList$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      this.filterList = res;
    });

    this.addressSearchCtrl.valueChanges.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      this.addressSearchCtrl.setErrors(null);
    });

    this.searchNameCtrl.valueChanges.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      this.searchNameCtrl.setErrors(null);
    });
  }

  closePropertySearchSheet() {
    this.clientDashboardState.openAddPropertySearchSheetValue = false;
    this.propertyFilterState.savedFiltersValue = null;
  }

  saveSearch() {
    if (this.saveSearchForm.invalid) {
      this.saveSearchForm.controls.searchName.setErrors({ message: 'Search name is required' });
      return;
    }
    if (this.location == undefined) {
      this.searchProperty();
      this.googleDir.first.addressComponents
        .pipe(
          debounceTime(500),
          take(1),
          finalize(() => this._changeDetectorRef.detectChanges())
        )
        .subscribe((res) => {
          if (!res) {
            this.addressSearchCtrl.setErrors({ invalid: true });
            return;
          }
          this.saveSearchForInvestor();
        });
    } else {
      this.saveSearchForInvestor();
    }
  }

  saveSearchForInvestor() {
    let params = {
      name: this.searchNameCtrl.value,
      investorId: this.investorId,
      location: this.location,
    };

    this.savedSearchService
      .addSavedSearchForClients(params)
      .pipe(
        takeUntil(this.onDestroyNotifier$),
        finalize(() => {
          this.location = null;
          this._changeDetectorRef.detectChanges();
        })
      )
      .subscribe(({ isLoading, status, cause }) => {
        this.isSaveSearchInProgress = isLoading;
        if (!status) {
          if (cause == 'duplicate') this.searchNameCtrl.setErrors({ duplicate: true });
          return;
        }
        this.clientDashboardState.openAddPropertySearchSheetValue = false;
      });
  }

  getAddressComponents(address?) {
    this.location = address;
    if (address?.fullAddress) this.addressSearchCtrl.setValue(address.fullAddress);
  }

  searchProperty() {
    this.googleDir.first.getPlaceFromGeocoder(this.autoCompleteInput);
    return;
  }

  get saveSearchFormControls() {
    return this.saveSearchForm.controls;
  }

  get searchNameCtrl(): FormControl {
    return this.saveSearchForm.controls.searchName as FormControl;
  }

  get addressSearchCtrl(): FormControl {
    return this.saveSearchForm.controls.addressSearch as FormControl;
  }

  ngOnDestroy() {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
    this.filtersState.detailedFilterListValue = null;
    this.clientDashboardState.savedSearchLocationValue = null;
  }
}
